import { Avatar as ChakraAvatar } from '@chakra-ui/react';
import { Pencil } from 'lucide-react';
import * as React from 'react';

import type { AvatarSize } from './avatar.kds';

export interface AvatarProps extends Omit<ChakraAvatar.RootProps, 'size' | 'variant'> {
  /** KDS size — `sm` (Small) or `lg` (Large). Default `sm`. */
  size?: AvatarSize;
  /** Profile image URL. */
  src?: string;
  /** Display name — derives initials (e.g. `"Jane Doe"` → `JD`) when the image is missing. */
  name?: string;
  /** Large only — hover overlay with edit icon. */
  editable?: boolean;
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(function Avatar(props, ref) {
  const { name, src, editable, size = 'sm', ...rest } = props;
  const showEditableOverlay = editable === true && size === 'lg';

  return (
    <ChakraAvatar.Root
      ref={ref}
      data-editable={showEditableOverlay ? '' : undefined}
      size={size}
      shape="full"
      {...rest}
    >
      <ChakraAvatar.Fallback>{name && getInitials(name)}</ChakraAvatar.Fallback>
      <ChakraAvatar.Image src={src} />
      {showEditableOverlay && (
        <span aria-hidden="true" data-avatar-edit-overlay="" data-testid="avatar-edit-overlay">
          <Pencil />
        </span>
      )}
    </ChakraAvatar.Root>
  );
});

const getInitials = (name: string) => {
  const names = name.trim().split(' ');
  const firstName = names[0] ?? '';
  const lastName = names.length > 1 ? (names.at(-1) ?? '') : '';
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }
  return firstName.charAt(0);
};
